import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData, ChartEvent, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { LoggerService } from '../../../services/logger.service';

interface ActiveStudentsHistoryRow {
  label: string;
  week_end_date: string;
  source: 'live' | 'backfill';
  active: number;
  front: number;
  middle: number;
  back: number;
  total_enrolled: number;
}

/**
 * Line chart of weekly Active Student Account counts, sourced from
 * ami_studio_metric_snapshot_weeks via /amt_dashboard/active-students-history.
 *
 * Active = the number of student_accounts with a Showed private lesson within
 * the engagement window (default 30 days). Front/Back lines show the same
 * count split by department per the Studio Business Report's threshold rule.
 */
@Component({
  selector: 'app-active-students-chart',
  templateUrl: './active-students-chart.component.html',
  styleUrls: ['./active-students-chart.component.css']
})
export class ActiveStudentsChartComponent implements OnInit, OnDestroy {

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  /** Starts true so the empty state cannot paint before the first load. */
  loading: boolean = true;
  hasData: boolean = false;

  private studioChangeSubscription?: Subscription;

  private _lastRows: ActiveStudentsHistoryRow[] = [];

  constructor(
    private _authService: AuthService,
    private _drupalRESTService: DrupalRESTService,
  ) {}

  ngOnInit(): void {
    this.loadHistory();
    this.studioChangeSubscription = this._authService.selectedStudioChanged
      .pipe(skip(1))
      .subscribe((studio) => {
        if (studio?.id) {
          this.loadHistory();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.studioChangeSubscription) {
      this.studioChangeSubscription.unsubscribe();
    }
  }

  lineChartType: ChartType = 'line';
  lineChartPlugins = [DataLabelsPlugin];

  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Active Student Accounts' },
        ticks: { precision: 0 },
      },
      x: {
        title: { display: true, text: 'Week' },
      },
    },
    plugins: {
      title: { display: true, text: 'Active Student Accounts Over Time (per week)' },
      legend: { display: true, position: 'bottom' },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          afterTitle: (items: any[]): string => {
            if (!items?.length) return '';
            const i = items[0].dataIndex;
            const row = this._lastRows[i];
            if (!row) return '';
            return `Week end ${row.week_end_date}`;
          },
        },
      },
    },
  };

  lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Active',
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.15)',
        tension: 0.25,
        fill: true,
      },
      {
        data: [],
        label: 'Front Dept',
        borderColor: '#43a047',
        backgroundColor: 'rgba(67, 160, 71, 0.10)',
        tension: 0.25,
        borderDash: [4, 4],
      },
      {
        data: [],
        label: 'Back Dept',
        borderColor: '#fb8c00',
        backgroundColor: 'rgba(251, 140, 0, 0.10)',
        tension: 0.25,
        borderDash: [4, 4],
      },
    ],
  };

  chartClicked(_: { event?: ChartEvent, active?: {}[] }): void {}
  chartHovered(_: { event?: ChartEvent, active?: {}[] }): void {}

  loadHistory(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      // Studio not resolved yet: stay in the loading state rather than
      // falling through to "No data available yet."
      this.loading = true;
      return;
    }

    this.loading = true;
    this.hasData = false;

    const endpoint = `/amt_dashboard/active-students-history/${studioId}`;
    const params = [{ parameter: 'limit', value: '26' }];
    this._drupalRESTService.httpGET(endpoint, params).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (!response?.success || !Array.isArray(response.data) || response.data.length === 0) {
          this.hasData = false;
          this._lastRows = [];
          this.applyEmpty();
          return;
        }

        this._lastRows = response.data as ActiveStudentsHistoryRow[];
        this.hasData = true;
        this.applyData();
      },
      error: (err) => {
        this.loading = false;
        this.hasData = false;
        LoggerService.error('Error loading active students history:', err);
        this.applyEmpty();
      },
    });
  }

  private applyData(): void {
    const labels = this._lastRows.map(r => r.label);
    const active = this._lastRows.map(r => r.active);
    const front = this._lastRows.map(r => r.front);
    const back = this._lastRows.map(r => r.back);

    this.lineChartData.labels = labels;
    this.lineChartData.datasets[0].data = active;
    this.lineChartData.datasets[1].data = front;
    this.lineChartData.datasets[2].data = back;
    this.chart?.update();
  }

  private applyEmpty(): void {
    this.lineChartData.labels = [];
    this.lineChartData.datasets.forEach(ds => ds.data = []);
    this.chart?.update();
  }
}
